import { fen2Yuan } from './amount';
import { isArray, isObject } from './type';

/** 下划线转驼峰 user_name -> userName */
export const toCamel = (str: string): string => str.replace(/_(\w)/g, (_, letter) => letter.toUpperCase());

/** 驼峰转下划线 userName -> user_name */
export const toSnake = (str: string): string => str.replace(/([A-Z])/g, '_$1').toLowerCase();

export type TransformType = 'toCamel' | 'toSnake';

export interface TransformOptions {
  type: TransformType;
  /** 是否深度转换，默认 true */
  deep?: boolean;
}

export type GenerObj = Record<string, any>;

export type FormatKey = {
  needParseIntKey?: string[];
  needParseDateKey?: string[];
  needParseBoolKey?: string[];
  needFen2YuanKey?: string[];
  transformer?: (_res: any) => any;
};

/** 转换对象的 key，支持深度转换 */
export const transformKeys = <T = GenerObj>(
  obj: any,
  options: TransformOptions,
): T => {
  const { type, deep = true } = options;
  const converter = type === 'toCamel' ? toCamel : toSnake;

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (isArray(obj)) {
    if (deep) {
      return obj.map((item: any) => transformKeys(item, options)) as T;
    }
    return obj as T;
  }

  if (isObject(obj)) {
    const result: GenerObj = {};

    for (const [key, value] of Object.entries(obj)) {
      const newKey = converter(key);

      if (deep) {
        if (isArray(value)) {
          result[newKey] = (value as any[]).map((item: any) => transformKeys(item, options));
        } else if (isObject(value)) {
          result[newKey] = transformKeys(value, options);
        } else {
          result[newKey] = value;
        }
      } else {
        result[newKey] = value;
      }
    }

    return result as T;
  }

  return obj;
};

/** 对象 key 下划线转驼峰 */
export const toCamelCase = <T = GenerObj>(obj: any, deep = true): T => transformKeys<T>(obj, { type: 'toCamel', deep });

/** 对象 key 驼峰转下划线 */
export const toSnakeCase = <T = GenerObj>(obj: any, deep = true): T => transformKeys<T>(obj, { type: 'toSnake', deep });

export const str2Date = (str: string): Date => new Date(str);

export interface TransformObjectOptions extends FormatKey {
  data: any;
  keyTransform?: TransformType;
  deep?: boolean;
}

const transformArrayValue = (options: TransformObjectOptions): any[] => {
  const { data, transformer, ...restOptions } = options;
  if (!isArray(data)) {
    return data;
  }

  return data.map((item: any) => {
    if (isArray(item)) {
      return transformArrayValue({ ...restOptions, data: item });
    } if (isObject(item)) {
      return transformObject({ ...restOptions, data: item });
    }
    return item;
  });
};

/** 转换对象，支持 key 转换和 value 类型转换 */
export const transformObject = (options: TransformObjectOptions): GenerObj => {
  const {
    data,
    keyTransform = 'toCamel',
    needParseIntKey = [],
    needParseDateKey = [],
    needParseBoolKey = [],
    needFen2YuanKey = [],
    transformer,
    deep = true,
  } = options;

  if (!data || !isObject(data)) {
    return data;
  }

  const res: GenerObj = {};
  const converter = keyTransform === 'toCamel' ? toCamel : toSnake;
  const { transformer: _, ...optionsWithoutTransformer } = options;

  for (const [key, value] of Object.entries(data)) {
    const transformedKey = converter(key);

    if (needParseIntKey.includes(transformedKey)) {
      res[transformedKey] = parseInt(value as string, 10);
    } else if (needParseDateKey.includes(transformedKey)) {
      res[transformedKey] = str2Date(value as string);
    } else if (needParseBoolKey.includes(transformedKey)) {
      res[transformedKey] = Boolean(Number(value));
    } else if (needFen2YuanKey.includes(transformedKey)) {
      res[transformedKey] = Number(fen2Yuan(value as string | number));
    } else {
      if (deep) {
        if (isArray(value)) {
          res[transformedKey] = transformArrayValue({
            ...optionsWithoutTransformer,
            data: value,
          });
        } else if (isObject(value)) {
          res[transformedKey] = transformObject({
            ...optionsWithoutTransformer,
            data: value,
          });
        } else {
          res[transformedKey] = value;
        }
      } else {
        res[transformedKey] = value;
      }
    }
  }

  if (transformer) {
    return transformer(res);
  }

  return res;
};
